// DevDb.jsx - developer-only raw table editor for the listings database.
// Protected by DEV_PASSWORD (separate from the client's admin password).
import React, { useEffect, useState, useCallback } from "react";
import "../css/listingAdmin.css";

const EDITABLE_FIELDS = [
  "id",
  "status",
  "address",
  "city",
  "state",
  "zip",
  "price",
  "beds",
  "baths",
  "sqft",
  "image",
  "url",
  "soldDate",
  "sort",
];

const WIDE_FIELDS = new Set(["id", "address", "image", "url"]);

const cellInputStyle = {
  width: "100%",
  minWidth: 70,
  padding: "6px 8px",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: 8,
  fontSize: 13,
  background: "#fff",
};

function Row({ row, onSaved, onDeleted, onError }) {
  const [draft, setDraft] = useState(row);
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(row), [row]);

  const dirty = EDITABLE_FIELDS.some(
    (f) => String(draft[f] ?? "") !== String(row[f] ?? "")
  );

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch("/api/dev/rows", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ row: draft }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Save failed");
      onSaved();
    } catch (e) {
      onError(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete row ${row.id} (${row.address || "no address"})?`)) return;
    setBusy(true);
    try {
      const r = await fetch("/api/dev/rows/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: row.id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Delete failed");
      onDeleted();
    } catch (e) {
      onError(e.message || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <tr style={dirty ? { background: "rgba(255, 235, 180, 0.35)" } : undefined}>
      {EDITABLE_FIELDS.map((f) => (
        <td key={f} style={{ padding: 3 }}>
          <input
            value={draft[f] ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
            style={{
              ...cellInputStyle,
              minWidth: WIDE_FIELDS.has(f) ? 180 : 70,
            }}
            readOnly={f === "id"}
            title={String(draft[f] ?? "")}
          />
        </td>
      ))}
      <td style={{ padding: 3, whiteSpace: "nowrap" }}>
        <button
          className="btn primary small"
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          style={{ marginRight: 6 }}
        >
          Save
        </button>
        <button className="btn secondary small" type="button" onClick={remove} disabled={busy}>
          Del
        </button>
      </td>
    </tr>
  );
}

export default function DevDb() {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [lastBackup, setLastBackup] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/dev/me", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        setUnlocked(!!j.authenticated);
      } catch {
        setUnlocked(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await fetch("/api/dev/rows", { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Load failed");
      setRows(Array.isArray(j.rows) ? j.rows : []);
      setLastBackup(j.lastBackup || null);
    } catch (e) {
      setError(e.message || "Load failed");
      if ((e.message || "").toLowerCase().includes("unauthorized")) setUnlocked(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked, load]);

  const login = async () => {
    setError("");
    try {
      const r = await fetch("/api/dev/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Login failed");
      setUnlocked(true);
      setPw("");
    } catch (e) {
      setError(e.message || "Login failed");
    }
  };

  const backupNow = async () => {
    setBackingUp(true);
    setError("");
    try {
      const r = await fetch("/api/dev/backup", { method: "POST", credentials: "include" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Backup failed");
      setLastBackup(j.at || new Date().toISOString());
    } catch (e) {
      setError(e.message || "Backup failed");
    } finally {
      setBackingUp(false);
    }
  };

  const logout = async () => {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include" }).catch(() => {});
    setUnlocked(false);
    setRows([]);
    setFilter("");
    setError("");
  };

  const addRow = async () => {
    const id = `dev-${Date.now().toString(36)}`;
    try {
      const r = await fetch("/api/dev/rows", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ row: { id, status: "active" } }),
      });
      if (!r.ok) throw new Error("Add failed");
      load();
    } catch (e) {
      setError(e.message || "Add failed");
    }
  };

  const q = filter.toLowerCase().trim();
  const visible = q
    ? rows.filter((r) =>
        EDITABLE_FIELDS.some((f) => String(r[f] ?? "").toLowerCase().includes(q))
      )
    : rows;

  if (checking) {
    return (
      <div className="interior-page listings-admin" style={{ padding: 40 }}>
        <p>Checking session...</p>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="interior-page listings-admin">
        <header className="page-hero">
          <div className="interior-inner">
            <h2>DB Editor</h2>
            <p className="subtitle">Developer access only.</p>
            <div className="ui-card listings-admin__auth">
              <div className="listings-admin__auth-grid">
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Dev password"
                  style={cellInputStyle}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                />
                <button className="btn primary" type="button" onClick={login}>
                  Unlock
                </button>
                {error ? <div className="listings-admin__error">{error}</div> : null}
              </div>
            </div>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="interior-page listings-admin">
      <header className="page-hero">
        <div className="interior-inner">
          <h2>DB Editor</h2>
          <p className="subtitle">
            {rows.length} rows. Edits save per row; changed rows are highlighted.
            {" "}Last sheet backup:{" "}
            {lastBackup ? new Date(lastBackup).toLocaleString() : "never"}.
          </p>

          <div className="listings-admin__actions" style={{ justifyContent: "flex-start" }}>
            <button className="btn secondary" type="button" onClick={addRow}>
              + Add Row
            </button>
            <button className="btn secondary" type="button" onClick={load}>
              Reload
            </button>
            <button className="btn secondary" type="button" onClick={backupNow} disabled={backingUp}>
              {backingUp ? "Backing up..." : "Backup Now"}
            </button>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter rows..."
              style={{ ...cellInputStyle, maxWidth: 260 }}
            />
            <button
              className="btn secondary"
              type="button"
              onClick={logout}
              style={{ marginLeft: "auto" }}
            >
              Logout
            </button>
          </div>

          {error ? (
            <div className="status-banner status-banner--error">{error}</div>
          ) : null}
        </div>
      </header>

      <main style={{ padding: "0 16px 60px" }}>
        <div className="ui-card" style={{ overflowX: "auto", padding: 10 }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                {EDITABLE_FIELDS.map((f) => (
                  <th
                    key={f}
                    style={{
                      textAlign: "left",
                      padding: "6px 4px",
                      textTransform: "uppercase",
                      fontSize: 11,
                      letterSpacing: ".08em",
                      color: "var(--text-muted)",
                    }}
                  >
                    {f}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  onSaved={load}
                  onDeleted={load}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
          {!visible.length ? <p style={{ margin: 10 }}>No rows.</p> : null}
        </div>
      </main>
    </div>
  );
}
